import { isCarsTrucks } from '@kijiji/category'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { SrpPagination } from '@/components/srp/pagination'
import { PopularRelatedKeywords } from '@/components/srp/popular-related-keywords/PopularRelatedKeywords'
import { SrpResultsSort } from '@/components/srp/results-sort'
import { RenderListingCard } from '@/components/srp/search-list/RenderListingCard'
import { RenderTopAds } from '@/components/srp/search-list/RenderTopAds'
import {
  type SearchListContainerProps,
  SearchListContainer,
} from '@/components/srp/search-list/styled'
import { SrpLoading } from '@/components/srp/srp-loading'
import { SRP_LISTING_LIMIT } from '@/constants/pageSettings'
import { getSearchCategoryFromSearchQuery } from '@/domain/category/getSearchCategoryFromSearchQuery'
import { getUserLocationFromSearchQuery } from '@/domain/location/getUserLocationFromSearchQuery'
import {
  getExtendedRadius,
  hasExtendedRadiusListings,
  isLowInventorySearch,
  isZeroInventorySearch,
} from '@/domain/lowInventory'
import { getCurrentPageFromPagination } from '@/domain/srp/getCurrentPageFromPaginationData'
import { AdSense } from '@/features/advertisement/components/adsense/AdSense'
import { ImageSeoJsonLdWrapper } from '@/features/seo/components/ImageSeoJsonLdWrapper'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { trackEvent } from '@/lib/ga'
import { getSearchPageType } from '@/lib/ga/utils/getSearchPageType'
import { type Listing } from '@/types/search'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'

const ZeroResultsActionBox = dynamic(
  () => import('@/components/srp/zero-results/').then((mod) => mod.ZeroResultsActionBox),
  { ssr: true } // to prevent layout shift
)

const MiniLeaderboardAdSlot = dynamic(
  () =>
    import('@/components-page/srp/advertisement/MiniLeaderboardAdSlot').then(
      (mod) => mod.MiniLeaderboardAdSlot
    ),
  { ssr: false }
)

const PaginationLinkContainer = dynamic(
  () => import('./styled').then((mod) => mod.PaginationLinkContainer),
  { ssr: false }
)

export type SearchListProps = {
  extendedRadiusListings: Listing[]
  totalPageCount: number
  isMobile?: boolean
  shouldRenderMapSRP?: boolean
  setShouldRenderMapSRP?: React.Dispatch<React.SetStateAction<boolean>>
}

const autosSearchListContainerGap: SearchListContainerProps['gap'] = {
  small: 'defaultSmall',
  xLarge: 'default',
}

export const SearchList = ({
  extendedRadiusListings = [],
  totalPageCount,
  isMobile,
  shouldRenderMapSRP,
  setShouldRenderMapSRP,
}: SearchListProps) => {
  const { colors, spacing } = useTheme()

  const { t } = useTranslation(['srp', 'common'])

  const { data, loadingResults: loading } = useGetSearchResultsData()

  const { pagination, searchQuery, results } = data || {}
  const { topListings: topAds = [], mainListings: listings = [] } = results || {}

  const globalKeyword = searchQuery?.keywords || ''
  const { totalCount: totalListings = 0, offset = 0, limit = SRP_LISTING_LIMIT } = pagination || {}

  const currentPage = getCurrentPageFromPagination({ offset, limit })

  const location = getUserLocationFromSearchQuery(searchQuery)
  const category = getSearchCategoryFromSearchQuery(searchQuery?.category)

  const extendedRadius = useMemo(
    () => getExtendedRadius(location?.area?.radius),
    [location?.area?.radius]
  )
  const isZeroSrp = useMemo(() => isZeroInventorySearch(totalListings), [totalListings])
  const isLowSrp = useMemo(() => isLowInventorySearch(totalListings), [totalListings])

  // We want to check the category the user is searching in, not the category
  // of the listing.
  const isCarsTrucksSearch = useMemo(() => isCarsTrucks(category.id), [category.id])
  const listingsGap = isCarsTrucksSearch ? autosSearchListContainerGap : undefined

  const searchPageType = useMemo(() => getSearchPageType(globalKeyword), [globalKeyword])

  useEffect(() => {
    if (isLowSrp) {
      trackEvent({ action: searchPageType, label: 'SuggestionBoxLoad' })
    }
  }, [isLowSrp, searchPageType])

  return (
    <Flex flexDirection="column" gap={spacing.default}>
      <>
        {loading ? (
          <SrpLoading variant="listing-card" />
        ) : (
          <>
            <ImageSeoJsonLdWrapper
              listings={listings.concat(topAds)}
              id="srp-listing-card-image-json-ld"
            />

            {shouldRenderMapSRP && (
              <Flex gap="20px" justifyContent="flex-end">
                <SrpResultsSort
                  shouldRenderResultsSortText={false}
                  setShouldRenderMapSRP={setShouldRenderMapSRP}
                  shouldRenderMapSRP={shouldRenderMapSRP}
                />
              </Flex>
            )}

            {/* Provincial Top Ads (PTAs) need to display on ZSRPs */}
            <RenderTopAds
              globalCategoryId={category.id}
              topAds={topAds}
              isMobile={isMobile}
              listingsGap={listingsGap}
            />

            <PopularRelatedKeywords
              categoryId={category.id.toString()}
              keywords={searchQuery?.keywords}
              location={{ id: location.id }}
              locationArea={location.area}
            />
            {isZeroSrp && <ZeroResultsActionBox />}

            {!!listings.length && (
              <SearchListContainer data-testid="srp-search-list" gap={listingsGap}>
                {listings.map((item, index) => (
                  <RenderListingCard key={item.id} item={item} index={index} isMobile={isMobile} />
                ))}
              </SearchListContainer>
            )}

            {isLowSrp && (
              <>
                {hasExtendedRadiusListings(extendedRadiusListings.length, extendedRadius) && (
                  <>
                    <Spacing mTop="2rem" mBottom="1rem">
                      <HeadlineText
                        color={colors.grey.primary}
                        size="xLarge"
                        weight="medium"
                        as="h2"
                      >
                        {t('srp:extended_listings.title', { extendedRadius })}
                      </HeadlineText>
                    </Spacing>

                    <SearchListContainer data-testid="srp-extended-list" gap={listingsGap}>
                      {extendedRadiusListings.map((item, index) => (
                        <RenderListingCard
                          key={item.id}
                          item={item}
                          index={index}
                          totalListings={extendedRadiusListings.length}
                          isMobile={isMobile}
                        />
                      ))}
                    </SearchListContainer>
                  </>
                )}

                <Spacing mTop="6rem">
                  <ZeroResultsActionBox />
                </Spacing>
              </>
            )}
          </>
        )}
      </>
      {/* Minileaderboard should not appear on zSRP */}
      <AdSense id="AFSBottom">{!!totalListings && <MiniLeaderboardAdSlot />}</AdSense>
      <PaginationLinkContainer justifyContent="center" alignItems="center">
        {totalPageCount > 1 && (
          <SrpPagination selectedIndex={currentPage} totalPageCount={totalPageCount} />
        )}
      </PaginationLinkContainer>
    </Flex>
  )
}
